import { Component, OnInit, NgZone, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError } from 'rxjs/operators';
import { throwError, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';

interface ProgressPayload {
  status_message: string;
  processed_count: number;
  passed_count?: number;
  skipped_count: number;
  total_count: number;
  progress_percentage: number;
  elapsed_time: string;
  error_count: number;
  latest_log: string;
  is_completed: boolean;
  is_running: boolean;
  logHistory: { text: string, isError: boolean }[];
}

interface PropertyResponse {
  id: number;
  site_name?: string;
  target_url?: string;
  property_name?: string;
  location?: string;
  station?: string;
  walk_minutes?: string;
  price_man_yen?: string;
  min_price_man_yen?: number;
  max_price_man_yen?: number;
  area_sqm?: string;
  zoning?: string;
  coverage_ratio?: string;
  far?: string;
  analysis_status?: string;
  structure_and_volume?: string;
  income_simulation?: string;
  analysis_reason?: string;
  is_saved: number;
  is_negative_profit?: boolean;
  raw_data?: any;
  calculated_tsubo_price?: number | null;
  disp_frontage?: string | null;
  disp_right_type?: string | null;
  disp_building_condition?: string | null;
  disp_height_district?: string | null;
}

interface LogEntry {
  type: 'info' | 'success' | 'warn' | 'error';
  message: string;
  timestamp: Date;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  searchForm: FormGroup;
  savedConditions: any[] = [];
  selectedConditionId: number | null = null;
  extractionProgress: ProgressPayload | null = null;
  isProcessing = false;
  processLogs: LogEntry[] = [];
  private abortController: AbortController | null = null;
  private lastKnownMaxId: number = 0;
  private formSub: Subscription | null = null;
  
  activeView: 'search' | 'saved' = 'search';
  displayAnalysis = false;
  extractedProperties: PropertyResponse[] = [];
  savedProperties: PropertyResponse[] = [];

  zoningOptions: string[] = [
    '第1種低層住居専用地域', '第2種低層住居専用地域',
    '第1種中高層住居専用地域', '第2種中高層住居専用地域',
    '第1種住居地域', '第2種住居地域', '準住居地域', '田園住居地域',
    '近隣商業地域', '商業地域',
    '準工業地域', '工業地域', '工業専用地域'
  ];

  secondaryFilters = {
    minCoverage: null as number | null,
    minFar: null as number | null,
    maxTsuboPrice: null as number | null,
    minFrontage: null as number | null,
    allowedZonings: [] as string[],
    ownershipOnly: false,
    noBuildingCondition: false,
    excludeHeightDistrict1: false
  };
  
  filteredExtractedProperties: PropertyResponse[] = [];
  selectedPropertyIds: Set<number> = new Set<number>();
  analyzedPropertyIds: Set<number> = new Set<number>();

  targetSites = [
    { id: 'suumo', name: 'SUUMO' },
    { id: 'homes', name: 'HOME\'S' },
    { id: 'rehouse', name: '三井のリハウス' },
    { id: 'livable', name: '東急リバブル' },
    { id: 'stepon', name: '住友不動産' },
    { id: 'nomu', name: 'ノムコム' }
  ];

  tokyo23Wards = [
    '千代田区', '中央区', '港区', '新宿区', '文京区', '台東区', '墨田区', '江東区',
    '品川区', '目黒区', '大田区', '世田谷区', '渋谷区', '中野区', '杉並区', '豊島区',
    '北区', '荒川区', '板橋区', '練馬区', '足立区', '葛飾区', '江戸川区'
  ];

  tokyoCities = [
    '八王子市', '立川市', '武蔵野市', '三鷹市', '府中市', '調布市', '町田市', '小金井市', '小平市', '日野市'
  ];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private zone: NgZone
  ) {
    const allSiteIds = this.targetSites.map(site => site.id);
    this.searchForm = this.fb.group({
      sites: [allSiteIds, Validators.required],
      propertyType: ['land', Validators.required], 
      buildingCondition: ['unspecified'],
      selectedAreas: [[], Validators.required],
      minPrice: [null, [Validators.min(0)]],
      maxPrice: [null, [Validators.min(0)]],
      minArea: [null, [Validators.min(0)]],
      maxArea: [null, [Validators.min(0)]],
      maxWalkMinutes: [null, [Validators.min(0)]],
      excludeBus: [false],
      minBuildingCoverage: [null, [Validators.min(0), Validators.max(100)]],
      minFloorAreaRatio: [null, [Validators.min(0)]],
      zones: [[]],
      minFrontage: [null, [Validators.min(0)]],
      ownershipOnly: [false],
      existingBuildingType: ['vacant'],
      maxExtractionCount: [null, [Validators.min(1)]]
    });
  }

  ngOnInit(): void {
    this.fetchSavedConditions();
    
    this.formSub = this.searchForm.valueChanges.subscribe(() => {
      if (this.extractedProperties.length > 0) {
        this.applySecondaryFilters();
      }
    });

    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.get<PropertyResponse[]>(`${baseUrl}/api/extracted-properties?is_saved=false&limit=1`)
      .subscribe({
        next: (data) => {
          this.lastKnownMaxId = data && data.length > 0 ? data[0].id : 0;
          this.checkCurrentProgressStatus();
        },
        error: () => {
          this.checkCurrentProgressStatus();
        }
      });
  }

  ngOnDestroy(): void {
    this.stopExtractionProcess();
    if (this.formSub) {
      this.formSub.unsubscribe();
    }
  }

  switchView(view: 'search' | 'saved'): void {
    this.activeView = view;
    window.scrollTo({ top: 0 });
    
    if (view === 'saved') {
      this.fetchSavedProperties();
    } else {
      this.fetchExtractedProperties();
    }
  }

  isNegotiableStatus(status?: string): boolean {
    return !!status && status.startsWith('指値交渉で可能');
  }

  isSavableStatus(status?: string): boolean {
    return status === '事業化可能' || this.isNegotiableStatus(status);
  }

  private extractNumber(val: any): number | null {
    if (val === null || val === undefined || val === '') return null;
    if (typeof val === 'number') return isNaN(val) ? null : val;
    const normalized = String(val)
      .replace(/[０-９．]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
      .replace(/,/g, '');
    const match = normalized.match(/-?\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  }

  private extractPriceManYen(prop: PropertyResponse): { min: number | null, max: number | null } {
    if (prop.min_price_man_yen !== null && prop.min_price_man_yen !== undefined) {
      const max = (prop.max_price_man_yen !== null && prop.max_price_man_yen !== undefined)
        ? prop.max_price_man_yen
        : prop.min_price_man_yen;
      return { min: prop.min_price_man_yen, max };
    }
    if (!prop.price_man_yen) return { min: null, max: null };

    const norm = String(prop.price_man_yen)
      .replace(/[０-９．]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
      .replace(/,/g, '');

    let totalMan = 0;
    let matched = false;

    const okuMatch = norm.match(/(\d+(?:\.\d+)?)\s*億/);
    if (okuMatch) {
      totalMan += parseFloat(okuMatch[1]) * 10000;
      matched = true;
    }
    const manMatch = norm.match(/(\d+(?:\.\d+)?)\s*万/);
    if (manMatch) {
      totalMan += parseFloat(manMatch[1]);
      matched = true;
    }

    if (matched) {
      return { min: totalMan, max: totalMan };
    }
    const fallback = this.extractNumber(norm);
    return { min: fallback, max: fallback };
  }

  private getRawField(raw: any, keys: string[]): string | null {
    if (!raw || typeof raw !== 'object') return null;
    for (const k of keys) {
      if (raw[k] !== undefined && raw[k] !== null && String(raw[k]).trim() !== '' && String(raw[k]).trim() !== '-' && String(raw[k]).trim() !== 'null') {
        return String(raw[k]).trim();
      }
    }
    return null;
  }

  private isZoningMatched(propZoning: string | undefined, allowedZonings: string[]): boolean {
    if (!propZoning) return false;
    const normProp = String(propZoning)
      .replace(/[０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
      .replace(/第一種/g, '第1種')
      .replace(/第二種/g, '第2種')
      .replace(/１種/g, '1種')
      .replace(/２種/g, '2種');

    return allowedZonings.some(selected => {
      switch (selected) {
        case '第1種低層住居専用地域': return /1種低層/.test(normProp);
        case '第2種低層住居専用地域': return /2種低層/.test(normProp);
        case '第1種中高層住居専用地域': return /1種中高層/.test(normProp);
        case '第2種中高層住居専用地域': return /2種中高層/.test(normProp);
        case '第1種住居地域': return /1種住居/.test(normProp);
        case '第2種住居地域': return /2種住居/.test(normProp);
        case '準住居地域': return /準住居/.test(normProp);
        case '田園住居地域': return /田園住居/.test(normProp);
        case '近隣商業地域': return /近隣商業/.test(normProp);
        case '商業地域': return normProp.replace(/近隣商業/g, '').includes('商業');
        case '準工業地域': return /準工業/.test(normProp);
        case '工業専用地域': return /工業専用/.test(normProp);
        case '工業地域': return normProp.replace(/準工業|工業専用/g, '').includes('工業');
        default: return normProp.includes(selected);
      }
    });
  }

  private processPropertyData(p: PropertyResponse): PropertyResponse {
    if (typeof p.raw_data === 'string') {
      try { p.raw_data = JSON.parse(p.raw_data); } catch(e) { p.raw_data = {}; }
    } else if (!p.raw_data) {
      p.raw_data = {};
    }

    const cleanArea = this.extractNumber(p.area_sqm);
    if (cleanArea !== null) {
      p.area_sqm = String(cleanArea);
    }

    const priceObj = this.extractPriceManYen(p);

    if (priceObj.min !== null && cleanArea !== null && cleanArea > 0) {
      const tsuboArea = cleanArea * 0.3025;
      p.calculated_tsubo_price = Math.round((priceObj.min / tsuboArea) * 10) / 10;
    } else {
      p.calculated_tsubo_price = null;
    }

    const rawFrontage = this.getRawField(p.raw_data, ['road_frontage_m', 'frontage', 'road_frontage', 'min_frontage', '接道間口', '間口']);
    const numFrontage = this.extractNumber(rawFrontage);
    p.disp_frontage = numFrontage !== null ? String(numFrontage) : rawFrontage;

    p.disp_right_type = this.getRawField(p.raw_data, ['right_type', 'rights', 'land_rights', 'ownership', '権利', '土地権利']);
    p.disp_building_condition = this.getRawField(p.raw_data, ['building_condition', 'condition', '建築条件']);
    p.disp_height_district = this.getRawField(p.raw_data, ['height_district', 'height_zone', '高度地区']);

    p.is_negative_profit = p.income_simulation 
      ? /E\.(粗)?利益:\s*-/.test(p.income_simulation) 
      : false;

    return p;
  }

  fetchExtractedProperties(): void {
    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.get<PropertyResponse[]>(`${baseUrl}/api/extracted-properties?is_saved=false`)
      .pipe(
        catchError((error) => {
          console.log('[Error] 一覧取得:', error);
          return throwError(() => new Error('一覧の取得に失敗しました'));
        })
      )
      .subscribe((data) => {
        this.extractedProperties = data
          .filter(p => p.id > this.lastKnownMaxId)
          .map(p => this.processPropertyData(p));

        this.applySecondaryFilters();
        this.displayAnalysis = this.extractedProperties.length > 0;
      });
  }

  fetchSavedProperties(): void {
    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.get<PropertyResponse[]>(`${baseUrl}/api/extracted-properties?is_saved=true`)
      .pipe(
        catchError((error) => {
          console.log('[Error] お気に入り一覧取得:', error);
          return throwError(() => new Error('お気に入り一覧の取得に失敗しました'));
        })
      )
      .subscribe((data) => {
        this.savedProperties = data.map(p => this.processPropertyData(p));
      });
  }

  onSaveProperty(result: PropertyResponse): void {
    if (result.is_saved === 1) return;
    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.put(`${baseUrl}/api/extracted-properties/${result.id}/save`, {})
      .subscribe(() => {
        result.is_saved = 1;
        this.snackBar.open('保存しました', '閉じる', { duration: 3000 });
      });
  }

  onDeleteSavedProperty(id: number): void {
    if (window.confirm('保存済みから削除してもよろしいですか？')) {
      const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
      this.http.delete(`${baseUrl}/api/extracted-properties/${id}`)
        .subscribe(() => {
          this.savedProperties = this.savedProperties.filter(p => p.id !== id);
          this.extractedProperties = this.extractedProperties.filter(p => p.id !== id);
          this.applySecondaryFilters();
          this.snackBar.open('削除しました', '閉じる', { duration: 3000 });
        });
    }
  }

  checkCurrentProgressStatus(): void {
    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.get<any>(`${baseUrl}/api/extract/status`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.log('[Error] ステータス取得エラー:', error);
          return throwError(() => new Error('ステータス取得失敗'));
        })
      )
      .subscribe((data) => {
        if (data && data.is_running) {
          this.startExtractionProcess();
        }
      });
  }

  startExtractionProcess(): void {
    this.isProcessing = true;
    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    
    this.abortController = new AbortController();
    this.processLogs = [];
    if (!this.extractionProgress) {
      this.extractionProgress = {
        status_message: '準備中...',
        processed_count: 0,
        passed_count: 0,
        skipped_count: 0,
        total_count: 0,
        progress_percentage: 0,
        elapsed_time: '00:00:00',
        error_count: 0,
        latest_log: '',
        is_completed: false
      } as any;
    }

    fetch(`${baseUrl}/api/extract/stream`, {
      signal: this.abortController.signal,
      credentials: 'include'
    }).then(async (response) => {
      if (!response.body) throw new Error('ReadableStream not supported');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;

          this.zone.run(() => {
            let parsedLine = trimmedLine;
            if (parsedLine.startsWith('data: ')) {
              parsedLine = parsedLine.substring(6).trim();
            }
            try {
              const data = JSON.parse(parsedLine);
              if (data.status_message) {
                this.extractionProgress = { ...this.extractionProgress, ...data };
              }
              
              if (data.latest_log && data.latest_log.trim() !== '') {
                const txt = data.latest_log.trim();
                let logType: 'info' | 'success' | 'warn' | 'error' = 'info';
                
                if (txt.includes('エラー') || txt.includes('失敗')) logType = 'error';
                else if (txt.includes('スキップ') || txt.includes('リトライ')) logType = 'warn';
                else if (txt.includes('完了') || txt.includes('成功')) logType = 'success';
                
                const lastLog = this.processLogs[this.processLogs.length - 1];
                if (!lastLog || lastLog.message !== txt) {
                  this.processLogs.push({
                    type: logType,
                    message: txt,
                    timestamp: new Date()
                  });
                  
                  setTimeout(() => {
                    const container = document.getElementById('log-scroll-container');
                    if (container) container.scrollTop = container.scrollHeight;
                  }, 50);
                }
              }

              if (data.is_completed || data.status_message === '抽出完了' || data.status_message === '強制停止されました' || data.status_message === 'エラー終了') {
                this.finalizeExtraction(data.status_message);
              }
            } catch (e) {}
          });
        }
      }

      this.zone.run(() => {
        if (this.isProcessing) {
          this.finalizeExtraction('接続終了');
        }
      });
    }).catch(error => {
      if (error.name !== 'AbortError') {
        this.zone.run(() => {
          console.error('Stream Error:', error);
          this.stopExtractionProcess();
        });
      }
    });
  }

  private finalizeExtraction(status: string): void {
    if (!this.isProcessing) return;
    this.stopExtractionProcess();
    
    if (status === '強制停止されました') {
      this.snackBar.open('処理を中止しました', '閉じる', { duration: 3000 });
    } else {
      this.snackBar.open(status === 'エラー終了' ? 'エラーが発生しました' : '抽出処理が完了しました', '閉じる', { duration: 3000 });
    }
    
    this.fetchExtractedProperties();
    
    setTimeout(() => {
      const analysisElement = document.getElementById('analysis-list-section');
      if (analysisElement) {
        analysisElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 200);
  }

  stopExtractionProcess(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isProcessing = false;
  }

  onCancelExtraction(): void {
    if (!window.confirm('実行中の処理を中止します。よろしいですか？')) return;
    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.post(`${baseUrl}/api/extract/cancel`, {}).subscribe({
      next: () => this.snackBar.open('停止要求を送信しました...', '閉じる', { duration: 3000 }),
      error: (err: HttpErrorResponse) => this.snackBar.open('停止処理に失敗しました', '閉じる', { duration: 3000 })
    });
  }

  fetchSavedConditions(): void {
    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.get<any[]>(`${baseUrl}/api/saved-conditions`)
      .pipe(
        catchError(() => throwError(() => new Error('保存済み条件の取得に失敗しました')))
      )
      .subscribe((data) => this.savedConditions = data);
  }

  onLoadCondition(conditionId: any): void {
    if (!conditionId) {
      this.selectedConditionId = null;
      return;
    }
    const cond = this.savedConditions.find(c => c.id === conditionId);
    if (!cond) return;
    try {
      const savedData = JSON.parse(cond.conditions_json);
      this.searchForm.patchValue({
        ...savedData,
        buildingCondition: 'unspecified',
        minBuildingCoverage: null,
        minFloorAreaRatio: null,
        zones: [],
        minFrontage: null,
        ownershipOnly: false
      });
      this.selectedConditionId = cond.id;
      this.snackBar.open('条件を読み込みました', '閉じる', { duration: 3000 });
    } catch (error) {
      this.snackBar.open('条件のパースに失敗しました', '閉じる', { duration: 4000 });
    }
  }

  onUpdateCondition(): void {
    if (!this.selectedConditionId) return;
    if (this.searchForm.invalid) {
      this.snackBar.open('入力内容にエラーがあります', '閉じる', { duration: 3000 });
      return;
    }
    const currentCond = this.savedConditions.find(c => c.id === this.selectedConditionId);
    if (!currentCond) return;

    const payload = {
      condition_name: currentCond.condition_name,
      conditions_json: JSON.stringify(this.searchForm.value)
    };

    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.put(`${baseUrl}/api/saved-conditions/${this.selectedConditionId}`, payload)
      .pipe(
        catchError(() => {
          this.snackBar.open('条件の保存に失敗しました', '閉じる', { duration: 5000 });
          return throwError(() => new Error('保存失敗'));
        })
      )
      .subscribe(() => {
        this.snackBar.open('条件を上書き保存しました', '閉じる', { duration: 3000 });
        this.fetchSavedConditions();
      });
  }

  onDeleteCondition(): void {
    if (!this.selectedConditionId) return;
    if (!window.confirm('この条件を削除してもよろしいですか？')) return;

    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.delete(`${baseUrl}/api/saved-conditions/${this.selectedConditionId}`)
      .pipe(
        catchError(() => {
          this.snackBar.open('条件の削除に失敗しました', '閉じる', { duration: 5000 });
          return throwError(() => new Error('削除失敗'));
        })
      )
      .subscribe(() => {
        this.snackBar.open('条件を削除しました', '閉じる', { duration: 3000 });
        this.selectedConditionId = null;
        this.fetchSavedConditions();
      });
  }

  toggleArrayItem(controlName: string, value: string, isChecked: boolean): void {
    const control = this.searchForm.get(controlName);
    if (!control) return;
    const currentValues = control.value as string[];
    if (isChecked) {
      if (!currentValues.includes(value)) control.setValue([...currentValues, value]);
    } else {
      control.setValue(currentValues.filter(v => v !== value));
    }
  }

  toggleAll(controlName: string, allValues: string[], checked: boolean): void {
    const control = this.searchForm.get(controlName);
    if (control) control.setValue(checked ? [...allValues] : []);
  }

  isAllSelected(controlName: string, allValues: string[]): boolean {
    const current = this.searchForm.get(controlName)?.value as string[];
    return allValues.length > 0 && allValues.every(v => current.includes(v));
  }

  toggleAll23Wards(checked: boolean): void {
    const control = this.searchForm.get('selectedAreas');
    if (!control) return;
    const currentValues = control.value as string[];
    if (checked) {
      control.setValue(Array.from(new Set([...currentValues, ...this.tokyo23Wards])));
    } else {
      control.setValue(currentValues.filter(v => !this.tokyo23Wards.includes(v)));
    }
  }

  isAll23WardsSelected(): boolean {
    const currentValues = this.searchForm.get('selectedAreas')?.value as string[];
    return this.tokyo23Wards.every(ward => currentValues.includes(ward));
  }

  onSaveConditions(): void {
    if (this.searchForm.invalid) {
      this.snackBar.open('入力内容にエラーがあります', '閉じる', { duration: 3000 });
      return;
    }
    const conditionName = window.prompt('保存する条件の名前を入力してください');
    if (!conditionName || conditionName.trim() === '') return;

    const payload = {
      condition_name: conditionName.trim(),
      conditions_json: JSON.stringify(this.searchForm.value)
    };

    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    this.http.post(`${baseUrl}/api/saved-conditions`, payload)
      .pipe(
        catchError(() => {
          this.snackBar.open('条件の保存に失敗しました', '閉じる', { duration: 5000 });
          return throwError(() => new Error('保存失敗'));
        })
      )
      .subscribe((res: any) => {
        this.snackBar.open('条件を新しく保存しました', '閉じる', { duration: 3000 });
        this.fetchSavedConditions();
        if (res && res.id) this.selectedConditionId = res.id;
      });
  }

  onSearch(): void {
    if (this.searchForm.invalid) return;
    
    this.displayAnalysis = false;
    this.selectedPropertyIds.clear();
    this.analyzedPropertyIds.clear(); 

    setTimeout(() => {
      const topElement = document.getElementById('dashboard-top');
      if (topElement) topElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    const baseUrl = environment && (environment as any).apiUrl ? (environment as any).apiUrl : '';
    
    const cleanConditions = {
      ...this.searchForm.value,
      buildingCondition: 'unspecified',
      minBuildingCoverage: null,
      minFloorAreaRatio: null,
      zones: [],
      minFrontage: null,
      ownershipOnly: false
    };

    const payload = {
      sites: this.searchForm.value.sites,
      task_type: 'search', 
      max_count: this.searchForm.value.maxExtractionCount,
      conditions: cleanConditions
    };

    this.processLogs = [];
    this.http.get<PropertyResponse[]>(`${baseUrl}/api/extracted-properties?is_saved=false&limit=1`).subscribe({
      next: (data) => {
        this.lastKnownMaxId = data && data.length > 0 ? data[0].id : 0;
        this.startExtractionRequest(baseUrl, payload);
      },
      error: () => this.startExtractionRequest(baseUrl, payload)
    });
  }

  private startExtractionRequest(baseUrl: string, payload: any): void {
    this.http.post(`${baseUrl}/api/extract/start`, payload)
      .pipe(
        catchError(() => {
          this.snackBar.open('抽出処理の開始に失敗しました', '閉じる', { duration: 4000 });
          return throwError(() => new Error('開始失敗'));
        })
      )
      .subscribe((res: any) => {
        if (res.status === 'already_running') {
          this.snackBar.open(res.message, '閉じる', { duration: 4000 });
        } else {
          this.snackBar.open('抽出処理を開始しました', '閉じる', { duration: 3000 });
          this.startExtractionProcess();
        }
      });
  }

  resetSecondaryFilters(): void {
    this.secondaryFilters = {
      minCoverage: null,
      minFar: null,
      maxTsuboPrice: null,
      minFrontage: null,
      allowedZonings: [],
      ownershipOnly: false,
      noBuildingCondition: false,
      excludeHeightDistrict1: false
    };
    this.applySecondaryFilters();
  }

  applySecondaryFilters(): void {
    const formVals = this.searchForm.value;
    const selectedAreas: string[] = formVals.selectedAreas || [];

    const maxWalk = this.extractNumber(formVals.maxWalkMinutes);
    const minPrice = this.extractNumber(formVals.minPrice);
    const maxPrice = this.extractNumber(formVals.maxPrice);
    const minArea = this.extractNumber(formVals.minArea);
    const maxArea = this.extractNumber(formVals.maxArea);

    const filterMinCov = this.extractNumber(this.secondaryFilters.minCoverage);
    const filterMinFar = this.extractNumber(this.secondaryFilters.minFar);
    const filterMaxTsubo = this.extractNumber(this.secondaryFilters.maxTsuboPrice);
    const filterMinFrontage = this.extractNumber(this.secondaryFilters.minFrontage);

    this.filteredExtractedProperties = this.extractedProperties.filter(prop => {
      if (selectedAreas.length > 0 && prop.location) {
        const areaMatched = selectedAreas.some(area => prop.location!.includes(area));
        if (!areaMatched) return false;
      }

      if (maxWalk !== null) {
        const walk = this.extractNumber(prop.walk_minutes);
        if (walk !== null && walk > maxWalk) return false;
      }
      
      const priceObj = this.extractPriceManYen(prop);
      if (priceObj.min === null) return false;
      if (minPrice !== null && priceObj.max !== null && priceObj.max < minPrice) return false;
      if (maxPrice !== null && priceObj.min !== null && priceObj.min > maxPrice) return false;

      const propArea = this.extractNumber(prop.area_sqm);
      if (propArea === null || propArea <= 0) return false;
      if (minArea !== null && propArea < minArea) return false;
      if (maxArea !== null && propArea > maxArea) return false;

      if (formVals.excludeBus) {
        const station = prop.station || '';
        const walkMinStr = prop.walk_minutes || '';
        if (station.includes('バス') || walkMinStr.includes('バス')) return false;
      }

      if (filterMinCov !== null) {
        const cov = this.extractNumber(prop.coverage_ratio);
        if (cov === null || cov < filterMinCov) return false;
      }
      if (filterMinFar !== null) {
        const far = this.extractNumber(prop.far);
        if (far === null || far < filterMinFar) return false;
      }
      if (this.secondaryFilters.allowedZonings.length > 0) {
        if (!this.isZoningMatched(prop.zoning, this.secondaryFilters.allowedZonings)) {
          return false;
        }
      }
      if (filterMaxTsubo !== null) {
        if (prop.calculated_tsubo_price === null || prop.calculated_tsubo_price === undefined) return false;
        if (prop.calculated_tsubo_price > filterMaxTsubo) return false;
      }
      if (filterMinFrontage !== null) {
        const frontage = this.extractNumber(prop.disp_frontage);
        if (frontage === null || frontage < filterMinFrontage) return false;
      }
      if (this.secondaryFilters.excludeHeightDistrict1 && prop.disp_height_district) {
        const hd = prop.disp_height_district;
        if (/第?(1|一|１)種/.test(hd)) return false;
      }
      if (this.secondaryFilters.ownershipOnly) {
        const rightType = prop.disp_right_type || '';
        if (rightType) {
          const isNotLease = /借地権ではありません|借地ではありません/.test(rightType);
          if (!isNotLease && /借地|地上権|賃借権|使用貸借/.test(rightType)) {
            return false;
          }
        }
      }
      if (this.secondaryFilters.noBuildingCondition) {
        const condition = prop.disp_building_condition || '';
        if (condition) {
          const isExplicitlyNone = /無|なし|ナシ|対象外|ではありません/.test(condition);
          if (!isExplicitlyNone && /有|あり|アリ|付/.test(condition)) {
            return false;
          }
        }
      }

      return true;
    });
    
    const filteredIds = new Set(this.filteredExtractedProperties.map(p => p.id));
    for (const id of Array.from(this.selectedPropertyIds)) {
      if (!filteredIds.has(id)) {
        this.selectedPropertyIds.delete(id);
        this.analyzedPropertyIds.delete(id);
      }
    }
  }
  
  toggleSelection(id: number): void {
    if (this.selectedPropertyIds.has(id)) {
      this.selectedPropertyIds.delete(id);
      this.analyzedPropertyIds.delete(id); 
    } else {
      this.selectedPropertyIds.add(id);
    }
  }
  
  selectAllProperties(checked: boolean): void {
    if (checked) {
      this.filteredExtractedProperties.forEach(p => this.selectedPropertyIds.add(p.id));
    } else {
      this.selectedPropertyIds.clear();
      this.analyzedPropertyIds.clear();
    }
  }
  
  isAllPropertiesSelected(): boolean {
    return this.filteredExtractedProperties.length > 0 && this.selectedPropertyIds.size === this.filteredExtractedProperties.length;
  }

  executeAnalysis(): void {
    this.selectedPropertyIds.forEach(id => this.analyzedPropertyIds.add(id));
  }
}